import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Space,
  Table,
} from "antd";
import Cookies from "js-cookie";
import { useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import {
  url_create_vanphong,
  url_get_list_vanphong,
  url_update_vanphong,
} from "../constants";
import { authAxios } from "../utils";

const { Search } = Input;

export default function SettingsVanPhong() {
  const [listVanPhong, setListVanPhong] = useState([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVanPhong, setEditingVanPhong] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchListVanPhong();
  }, []);

  const fetchListVanPhong = async () => {
    try {
      setLoading(true);
      const res = await authAxios(Cookies.get("token")).get(
        url_get_list_vanphong,
      );
      setListVanPhong(res.data || []);
    } catch (error) {
      console.error(error);
      message.error("Không thể tải danh sách văn phòng");
    } finally {
      setLoading(false);
    }
  };

  // Filter theo keyword
  const dataSource = useMemo(() => {
    if (!keyword) return listVanPhong;
    return listVanPhong.filter((item) =>
      item.name?.toLowerCase().includes(keyword.toLowerCase()),
    );
  }, [listVanPhong, keyword]);

  const handleOpenModal = (vp = null) => {
    setEditingVanPhong(vp);
    if (vp) {
      form.setFieldsValue({
        name: vp.name,
        is_active: vp.is_active,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ is_active: true });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingVanPhong(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      if (editingVanPhong) {
        await authAxios(Cookies.get("token")).put(
          `${url_update_vanphong}/${editingVanPhong.id}`,
          {
            ...values,
          },
        );
        message.success("Cập nhật văn phòng thành công");
      } else {
        await authAxios(Cookies.get("token")).post(url_create_vanphong, values);
        message.success("Thêm văn phòng thành công");
      }
      handleCloseModal();
      fetchListVanPhong();
    } catch (error) {
      console.error(error);
      message.error(
        editingVanPhong
          ? "Không thể cập nhật văn phòng"
          : "Không thể tạo văn phòng",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const columns = [
    {
      title: "STT",
      key: "stt",
      width: 70,
      align: "center",
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "Tên Văn phòng",
      dataIndex: "name",
      key: "name",
      align: "center",
    },
    {
      title: "Slug",
      dataIndex: "slug_name",
      key: "slug_name",
      align: "center",
    },
    {
      title: "Ngày tạo",
      dataIndex: "created",
      key: "created",
      align: "center",
      render: (text) => (text ? dayjs(text).format("DD/MM/YYYY") : "-"),
    },
    {
      title: "Trạng thái",
      dataIndex: "is_active",
      key: "is_active",
      align: "center",
      render: (isActive) => <span>{isActive ? "Kích hoạt" : "Khóa"}</span>,
    },
    {
      title: "Thao tác",
      key: "action",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleOpenModal(record)}
          >
            Sửa
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col sm={14}>
          <Button type="primary" onClick={() => handleOpenModal()}>
            <PlusOutlined />
            Thêm văn phòng
          </Button>
        </Col>
        <Col sm={10}>
          <Search
            placeholder="Nhập từ khóa tìm kiếm"
            enterButton="Tìm kiếm"
            allowClear
            onSearch={(value) => {
              setKeyword(value.trim());
              setCurrentPage(1);
            }}
          />
        </Col>
      </Row>

      <Table
        rowKey="id"
        bordered
        loading={loading}
        dataSource={dataSource}
        // @ts-ignore
        columns={columns}
        onChange={handleTableChange}
        scroll={{ x: 1000, y: "70vh" }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
          total: dataSource.length,
        }}
      />

      <Modal
        title={editingVanPhong ? "Chỉnh sửa văn phòng" : "Thêm văn phòng mới"}
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label="Tên văn phòng"
            name="name"
            rules={[
              { required: true, message: "Vui lòng nhập tên văn phòng" },
              { min: 2, message: "Tên văn phòng phải có ít nhất 2 ký tự" },
            ]}
          >
            <Input placeholder="Nhập tên văn phòng" />
          </Form.Item>

          <Form.Item name="is_active" valuePropName="checked" label="Kích hoạt">
            <Checkbox>Kích hoạt</Checkbox>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={handleCloseModal}>Hủy</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingVanPhong ? "Cập nhật" : "Thêm mới"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
